/* eslint-disable react/require-default-props */
/* eslint-disable max-len */
import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Button, Dropdown, Modal, Select, Toggle,
} from "@xfrontend/xui-react-plus";
import RiskLevelTag from "#/modules/common/RiskLevelTag";
import { useLanguage } from "#/i18n/LanguageContext";
import { getPolicyLocale } from "#/i18n/policyTranslations";
import { sortByRiskLevelDesc } from "#/types/xecguard";

export type PolicyOption = {
  id: string;
  policyName: string;
  name: string;
  description: string;
  riskLevel: number;
  category: "default" | "custom";
  updatedAt: string;
};

type PolicySelectorModalProps = {
  show: boolean;
  onClose: () => void;
  policies: PolicyOption[];
  selectedIds: string[];
  onConfirm: (selectedIds: string[]) => void;
  title?: string;
};

const StyledModal = styled(Modal)`
  .modal-dialog {
    width: 640px;
    max-width: calc(100% - 32px);
  }

  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border: 1px solid var(--x-purple-500, #494358);
  }
`;

const Subtitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 12px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 8px;
`;

const GroupTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--x-gray-200, #f6f6f7);
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--x-purple-400, #6a6280);
  font-size: 12px;
  color: var(--x-gray-200, #f6f6f7);
`;

const PolicyItem = styled.div`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  margin-bottom: 8px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const PolicyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PolicyName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
`;

const PolicyDesc = styled.p`
  font-size: 13px;
  color: var(--x-gray-400, #cfd0d3);
  margin: 4px 0;
  line-height: 1.5;
`;

const PolicyMeta = styled.div`
  font-size: 12px;
  color: var(--x-gray-500, #a9a9ac);
  display: flex;
  gap: 16px;
`;

const FooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const HIDDEN_POLICY_IDS = new Set(["default_context_grounding_validation"]);
const MAX_DEFAULT_POLICIES = 5;
const MAX_CUSTOM_POLICIES = 2;

const PolicySelectorModal = ({
  show,
  onClose,
  policies,
  selectedIds,
  onConfirm,
  title,
}: PolicySelectorModalProps): JSX.Element => {
  const { t } = useTranslation("common");
  const { t: tPolicySpec } = useTranslation("policySpec");
  const resolvedTitle = title ?? tPolicySpec("selector.title", { defaultValue: "Configure Policies" });
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { language } = useLanguage();
  const getTranslated = (policy: PolicyOption) => getPolicyLocale(language, policy.policyName, {
    name: policy.name,
    description: policy.description,
  });

  const visiblePolicies = policies.filter((p) => !HIDDEN_POLICY_IDS.has(p.id));

  const handleOpen = () => {
    setTempSelectedIds([...selectedIds]);
    setSearchQuery("");
  };

  const defaultPolicies = visiblePolicies.filter((p) => p.category === "default").sort(sortByRiskLevelDesc);
  const customPolicies = visiblePolicies.filter((p) => p.category === "custom").sort(sortByRiskLevelDesc);

  const togglePolicy = (id: string, category: "default" | "custom") => {
    setTempSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (category === "default") {
        const currentDefaultCount = defaultPolicies.filter((p) => prev.includes(p.id)).length;
        if (currentDefaultCount >= MAX_DEFAULT_POLICIES) return prev;
      }
      if (category === "custom") {
        const currentCustomCount = customPolicies.filter((p) => prev.includes(p.id)).length;
        if (currentCustomCount >= MAX_CUSTOM_POLICIES) return prev;
      }
      return [...prev, id];
    });
  };
  const query = searchQuery.trim().toLowerCase();
  const filteredDefault = defaultPolicies.filter((p) => getTranslated(p).name.toLowerCase().includes(query));
  const filteredCustom = customPolicies.filter((p) => getTranslated(p).name.toLowerCase().includes(query));
  const selectedDefaultCount = defaultPolicies.filter((p) => tempSelectedIds.includes(p.id)).length;
  const selectedCustomCount = customPolicies.filter((p) => tempSelectedIds.includes(p.id)).length;

  return (
    <StyledModal
      title={resolvedTitle}
      show={show}
      onClose={onClose}
      showCloseBtn
      restoreFocus={false}
      onEnter={handleOpen}
    >
      <Modal.Body>
        <Subtitle>{tPolicySpec("selector.policyList", { defaultValue: "Policy List" })}</Subtitle>
        <Select
          name="policy-search"
          placeholder={t("policySearch.placeholder")}
          withSearchIndicator
          options={[]}
          inputValue={searchQuery}
          onInputChange={(val: string) => setSearchQuery(val)}
          menuIsOpen={false}
          isClearable={false}
        />

        <GroupHeader style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <GroupTitle>{tPolicySpec("selector.defaultPolicies", { max: MAX_DEFAULT_POLICIES })}</GroupTitle>
            <CountBadge>{selectedDefaultCount}</CountBadge>
          </div>
        </GroupHeader>
        {filteredDefault.map((policy) => {
          const isChecked = tempSelectedIds.includes(policy.id);
          const isDisabled = !isChecked && selectedDefaultCount >= MAX_DEFAULT_POLICIES;
          const translated = getTranslated(policy);
          return (
            <PolicyItem key={policy.id} style={{ opacity: isDisabled ? 0.5 : 1 }}>
              <Toggle
                name={`policy-${policy.id}`}
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => togglePolicy(policy.id, "default")}
              />
              <PolicyInfo>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
                }}
                >
                  <RiskLevelTag level={policy.riskLevel} />
                  <PolicyName>{translated.name}</PolicyName>
                </div>
                <PolicyDesc>{translated.description}</PolicyDesc>
                <PolicyMeta>
                  <span>
                    {`${tPolicySpec("selector.updatedTime")}  `}
                    {policy.updatedAt}
                  </span>
                  <span>
                    {`${tPolicySpec("selector.policyId")}  `}
                    {policy.id}
                  </span>
                </PolicyMeta>
              </PolicyInfo>
            </PolicyItem>
          );
        })}

        <GroupHeader style={{ marginTop: 16 }}>
          <GroupTitle>{tPolicySpec("selector.customPolicies", { max: MAX_CUSTOM_POLICIES })}</GroupTitle>
          <CountBadge>{selectedCustomCount}</CountBadge>
        </GroupHeader>
        {filteredCustom.map((policy) => {
          const isChecked = tempSelectedIds.includes(policy.id);
          const isDisabled = !isChecked && selectedCustomCount >= MAX_CUSTOM_POLICIES;
          const translated = getTranslated(policy);
          return (
            <PolicyItem key={policy.id} style={{ opacity: isDisabled ? 0.5 : 1 }}>
              <Toggle
                name={`policy-${policy.id}`}
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => togglePolicy(policy.id, "custom")}
              />
              <PolicyInfo>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
                }}
                >
                  <RiskLevelTag level={policy.riskLevel} />
                  <PolicyName>{translated.name}</PolicyName>
                </div>
                <PolicyDesc>{translated.description}</PolicyDesc>
                <PolicyMeta>
                  <span>
                    {`${tPolicySpec("selector.updatedTime")}  `}
                    {policy.updatedAt}
                  </span>
                  <span>
                    {`${tPolicySpec("selector.policyId")}  `}
                    {policy.id}
                  </span>
                </PolicyMeta>
              </PolicyInfo>
            </PolicyItem>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <FooterContent>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>
              {t("actions.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onConfirm(tempSelectedIds);
                onClose();
              }}
            >
              {t("actions.confirm")}
            </Button>
          </div>
        </FooterContent>
      </Modal.Footer>
    </StyledModal>
  );
};

export default PolicySelectorModal;
